import {useAppContext} from "../../context/AppContext";
import React, {useEffect, useMemo, useState} from "react";
import {Select, Button, Space} from "antd";
import {UpOutlined, DownOutlined} from "@ant-design/icons";
import {GetMessageCodeText} from "../outputs/MessageCodes";
import {readPlot2, writePlot2} from "./plot2Store";

// A reusable River / message-RS / all-RS / nav selector bar over getXsPlots(). It
// manages selection (persisted via plot2Store under persistKey) and calls
// onSelect(item) with the currently selected xs_plots item (or null). `type`
// ('cross_section' | 'structure') filters which items are browsable. The message-RS
// list follows the globally selected message (selectedErrorId). Used by the UI tab's
// all-RS mode; Plot-2 keeps its own copy of this logic.
const idOf = (x) => `${x.river}___${x.reach}__${x.type}:${x.cross_section_value}`
const matchesRs = (m, x) => String(m) === String(x.cross_section) || String(m).includes(String(x.cross_section_value))

export default function XsBrowserBar({type, persistKey, onSelect}) {
    const {getXsPlots, selectedOperation, selectedErrorId} = useAppContext()

    const xs = getXsPlots() || {items: []}
    const items = useMemo(
        () => (xs.items || []).filter(x => !type || x.type === type),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [xs.items, type]
    )

    const saved = readPlot2(persistKey)
    const [selectedId, setSelectedId] = useState(saved.selectedId ?? null)

    useEffect(() => {
        writePlot2(persistKey, {selectedId})
    }, [persistKey, selectedId]);

    const showItems = items

    // Message RS list follows the globally selected message (sidebar), grouped at the
    // top of / shown in the dedicated message-RS dropdown.
    const cellMsgRs = selectedOperation?.data?.result
        ?.find(x => GetMessageCodeText(selectedOperation?.key, x.error_id) === selectedErrorId)
        ?.data?.map(r => r.cross_section) || []
    const isMessageXs = (x) => cellMsgRs.some(m => matchesRs(m, x))
    const msgEntries = showItems.filter(isMessageXs)

    // Default selection: keep a valid restored one, else first message XS, else first XS.
    useEffect(() => {
        if (selectedId && showItems.some(x => idOf(x) === selectedId)) return
        const first = msgEntries[0] || showItems[0]
        setSelectedId(first ? idOf(first) : null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showItems.length]);

    const entry = showItems.find(x => idOf(x) === selectedId) || null

    useEffect(() => {
        if (onSelect) onSelect(entry)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId, showItems.length]);

    const stepIn = (list, delta) => {
        const i = list.findIndex(x => idOf(x) === selectedId)
        const n = (i < 0 && delta > 0) ? list[0] : list[i + delta]
        if (n) setSelectedId(idOf(n))
    }
    const msgIdx = msgEntries.findIndex(x => idOf(x) === selectedId)
    const allIdx = showItems.findIndex(x => idOf(x) === selectedId)

    const renderOption = (x) => (
        <Select.Option key={idOf(x)} value={idOf(x)}>
            {x.cross_section}{x.type === 'structure' ? ' · Str' : ''}
        </Select.Option>
    )

    return (
        <div style={{display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center'}}>
            <Space size={0}>
                <Select
                    size={'small'} style={{width: 140}} showSearch
                    value={msgEntries.some(x => idOf(x) === selectedId) ? selectedId : undefined}
                    onChange={setSelectedId}
                    disabled={msgEntries.length === 0}
                    placeholder={msgEntries.length ? `${selectedErrorId || 'Msg'} RS` : 'No message RS'}
                    optionFilterProp={'children'}
                >
                    {msgEntries.map(renderOption)}
                </Select>
                <Button size={'small'} icon={<UpOutlined/>} onClick={() => stepIn(msgEntries, -1)} disabled={msgIdx <= 0}/>
                <Button size={'small'} icon={<DownOutlined/>} onClick={() => stepIn(msgEntries, 1)} disabled={msgEntries.length === 0 || msgIdx >= msgEntries.length - 1}/>
            </Space>
            <span style={{fontSize: 11, color: '#888', flexShrink: 0}}>All RS:</span>
            <Space size={0}>
                <Select
                    size={'small'} style={{width: 200}} showSearch value={selectedId}
                    onChange={setSelectedId} placeholder={'Cross section'} optionFilterProp={'children'}
                >
                    {showItems.map(renderOption)}
                </Select>
                <Button size={'small'} icon={<UpOutlined/>} onClick={() => stepIn(showItems, -1)} disabled={allIdx <= 0}/>
                <Button size={'small'} icon={<DownOutlined/>} onClick={() => stepIn(showItems, 1)} disabled={showItems.length === 0 || allIdx >= showItems.length - 1}/>
            </Space>
        </div>
    )
}
